// ============================================================
// utils/fotocheckPdf.ts
// Genera PDFs de fotochecks con QR (DNI). Tamaño PVC estándar.
//   • Horizontal: 86 × 54 mm — 2 col × 5 filas = 10 por A4
//   • Vertical:   54 × 86 mm — 3 col × 3 filas = 9  por A4
// ============================================================

import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { FotocheckEstudiante } from 'types/fotocheck.types';
import { getConfig } from 'utils/appConfig';
import { catalogStorage } from 'utils/catalogStorage';
import { httpClient } from 'utils/httpClient';

// ── Tipos ─────────────────────────────────────────────────────

export type OrientacionFotocheck = 'horizontal' | 'vertical';

// ── Constantes geométricas ────────────────────────────────────

const A4_W = 210;   // mm
const A4_H = 297;

// Tarjeta horizontal (paisaje)
const H_CARD_W = 86;
const H_CARD_H = 54;
const H_COLS   = 2;
const H_ROWS   = 5;
const H_MARGIN_X = (A4_W - H_CARD_W * H_COLS) / (H_COLS + 1); // ≈ 12.67 mm
const H_MARGIN_Y = (A4_H - H_CARD_H * H_ROWS) / (H_ROWS + 1); // ≈ 4.5 mm

// Tarjeta vertical (retrato)
const V_CARD_W = 54;
const V_CARD_H = 86;
const V_COLS   = 3;
const V_ROWS   = 3;
const V_MARGIN_X = (A4_W - V_CARD_W * V_COLS) / (V_COLS + 1); // = 12 mm
const V_MARGIN_Y = (A4_H - V_CARD_H * V_ROWS) / (V_ROWS + 1); // ≈ 9.75 mm

// ── Helpers ───────────────────────────────────────────────────

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload  = () => resolve(r.result as string);
    r.onerror = reject;
    r.readAsDataURL(blob);
  });
}

/**
 * Resuelve `fotoPerfil` a un dataURL base64 listo para `doc.addImage`.
 *
 *   data:image/...     → tal cual
 *   http(s)://...      → fetch sin auth (foto heredada externa)
 *   usuarios/<key>     → fetch con JWT a /usuarios/{id}/foto (clave MinIO)
 *   null o falla       → null (caller dibuja iniciales)
 */
async function resolverFotoDataUrl(
  fotoPerfil: string | null,
  estudianteId: number,
): Promise<string | null> {
  if (!fotoPerfil) return null;
  if (fotoPerfil.startsWith('data:')) return fotoPerfil;

  try {
    if (fotoPerfil.startsWith('http://') || fotoPerfil.startsWith('https://')) {
      const res = await fetch(fotoPerfil, { mode: 'cors' });
      if (!res.ok) return null;
      return await blobToDataUrl(await res.blob());
    }

    // Key MinIO ("usuarios/...") → endpoint autenticado
    const objectUrl = await httpClient.fetchBlobUrl(`/usuarios/${estudianteId}/foto`);
    try {
      const res  = await fetch(objectUrl);
      const blob = await res.blob();
      return await blobToDataUrl(blob);
    } finally {
      URL.revokeObjectURL(objectUrl);
    }
  } catch {
    return null;
  }
}

/** Genera un dataURL PNG con el QR del DNI. */
async function generarQR(texto: string): Promise<string> {
  return QRCode.toDataURL(texto, {
    errorCorrectionLevel: 'M',
    margin: 1,
    width: 256,
    color: { dark: '#1e293b', light: '#ffffff' },
  });
}

/** Iniciales para el avatar fallback. */
function iniciales(nombres: string, apellidos: string): string {
  return `${(nombres[0] ?? '').toUpperCase()}${(apellidos[0] ?? '').toUpperCase()}`;
}

/** Color de fondo del avatar de iniciales según el DNI (consistente por estudiante). */
function colorAvatar(dni: string): { fill: [number, number, number]; text: [number, number, number] } {
  const palette: Array<{ fill: [number, number, number]; text: [number, number, number] }> = [
    { fill: [219, 234, 254], text: [29,  78, 216] }, // azul
    { fill: [220, 252, 231], text: [22, 101,  52] }, // verde
    { fill: [254, 243, 199], text: [146, 64,  14] }, // ámbar
    { fill: [243, 232, 255], text: [107, 33, 168] }, // morado
    { fill: [254, 226, 226], text: [153, 27,  27] }, // rojo
    { fill: [207, 250, 254], text: [21,  94, 117] }, // cyan
  ];
  const seed = dni.split('').reduce((s, c) => s + c.charCodeAt(0), 0);
  return palette[seed % palette.length];
}

// ── Render de un fotocheck ────────────────────────────────────

interface RenderArgs {
  doc:      jsPDF;
  est:      FotocheckEstudiante;
  qrData:   string;
  fotoData: string | null;
  x:        number;
  y:        number;
  w:        number;
  h:        number;
  institucion: string;
  selloData:   string | null;
}

function renderFotocheck({ doc, est, qrData, fotoData, x, y, w, h, institucion, selloData }: RenderArgs): void {
  // ── Tarjeta (borde + fondo) ───────────────────────────────
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.2);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, 2.5, 2.5, 'FD');

  // ── Banda superior (institución) ──────────────────────────
  const HEAD_H = 8;
  doc.setFillColor(30, 64, 175); // blue-800
  doc.roundedRect(x, y, w, HEAD_H, 2.5, 2.5, 'F');
  doc.setFillColor(30, 64, 175);
  doc.rect(x, y + HEAD_H - 2.5, w, 2.5, 'F');

  // Sello pequeño a la izquierda (si existe)
  if (selloData) {
    try { doc.addImage(selloData, 'PNG', x + 1.5, y + 1, 6, 6); } catch { /* ignore */ }
  }

  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  const nombreInst = institucion.length > 38 ? `${institucion.slice(0, 36)}…` : institucion;
  doc.text(nombreInst.toUpperCase(), x + (selloData ? 9 : 2.5), y + 4);
  doc.setFontSize(5.5);
  doc.setFont('helvetica', 'normal');
  doc.text(`Año Escolar ${est.anio}`, x + (selloData ? 9 : 2.5), y + 6.6);

  // ── Foto / Avatar (izquierda) ─────────────────────────────
  const PHOTO_X = x + 3;
  const PHOTO_Y = y + HEAD_H + 3;
  const PHOTO_W = 22;
  const PHOTO_H = 28;

  if (fotoData) {
    try {
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(PHOTO_X, PHOTO_Y, PHOTO_W, PHOTO_H, 1.5, 1.5, 'F');
      doc.addImage(fotoData, 'JPEG', PHOTO_X, PHOTO_Y, PHOTO_W, PHOTO_H);
    } catch {
      drawAvatarIniciales(doc, est, PHOTO_X, PHOTO_Y, PHOTO_W, PHOTO_H);
    }
  } else {
    drawAvatarIniciales(doc, est, PHOTO_X, PHOTO_Y, PHOTO_W, PHOTO_H);
  }

  // ── Datos del estudiante (centro) ─────────────────────────
  const TXT_X = PHOTO_X + PHOTO_W + 3;
  const TXT_W = w - PHOTO_W - 5 - 22; // descontando QR
  let cy = PHOTO_Y + 2;

  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  const apellidos = doc.splitTextToSize(est.estudianteApellidos.toUpperCase(), TXT_W);
  doc.text(apellidos.slice(0, 2), TXT_X, cy + 2);
  cy += apellidos.length > 1 ? 6 : 3.5;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  const nombres = doc.splitTextToSize(est.estudianteNombres, TXT_W);
  doc.text(nombres.slice(0, 1), TXT_X, cy + 2);
  cy += 5;

  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.15);
  doc.line(TXT_X, cy, TXT_X + TXT_W, cy);
  cy += 2.5;

  // DNI
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(5);
  doc.text('DNI', TXT_X, cy);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(7);
  doc.text(est.estudianteDni, TXT_X + 7, cy);
  cy += 3.5;

  // Grado/Sección
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5);
  doc.text('GRADO', TXT_X, cy);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  const gradoSec = `${est.gradoNombre} — ${est.seccionNombre}`;
  doc.text(gradoSec, TXT_X + 7, cy);
  cy += 3.5;

  // Nivel
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5);
  doc.text('NIVEL', TXT_X, cy);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);
  doc.text(est.nivel, TXT_X + 7, cy);

  // ── QR (derecha) ──────────────────────────────────────────
  const QR_SIZE = 22;
  const QR_X = x + w - QR_SIZE - 3;
  const QR_Y = PHOTO_Y;
  try { doc.addImage(qrData, 'PNG', QR_X, QR_Y, QR_SIZE, QR_SIZE); } catch { /* ignore */ }

  // Etiqueta del QR
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(4.5);
  doc.text('Escanear DNI', QR_X + QR_SIZE / 2, QR_Y + QR_SIZE + 2.5, { align: 'center' });

  // ── Footer (código de matrícula / SIAGIE) ─────────────────
  const FOOTER_Y = y + h - 3;
  doc.setTextColor(148, 163, 184);
  doc.setFontSize(4.5);
  doc.setFont('helvetica', 'normal');
  const partes: string[] = [];
  if (est.codigoMatricula)        partes.push(`Matr: ${est.codigoMatricula}`);
  if (est.estudianteCodigoSiagie) partes.push(`SIAGIE: ${est.estudianteCodigoSiagie}`);
  if (partes.length) {
    doc.text(partes.join('   ·   '), x + 3, FOOTER_Y);
  }
}

function drawAvatarIniciales(doc: jsPDF, est: FotocheckEstudiante, x: number, y: number, w: number, h: number): void {
  const c = colorAvatar(est.estudianteDni);
  doc.setFillColor(c.fill[0], c.fill[1], c.fill[2]);
  doc.roundedRect(x, y, w, h, 1.5, 1.5, 'F');
  doc.setTextColor(c.text[0], c.text[1], c.text[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(Math.min(w, h) * 0.55);
  doc.text(iniciales(est.estudianteNombres, est.estudianteApellidos), x + w / 2, y + h / 2 + Math.min(w, h) * 0.18, { align: 'center' });
}

// ── Render vertical (54 × 86 mm) ──────────────────────────────

function renderFotocheckVertical({ doc, est, qrData, fotoData, x, y, w, h, institucion, selloData }: RenderArgs): void {
  // Tarjeta
  doc.setDrawColor(203, 213, 225);
  doc.setLineWidth(0.2);
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(x, y, w, h, 2.5, 2.5, 'FD');

  // Banda superior (institución)
  const HEAD_H = 9;
  doc.setFillColor(30, 64, 175);
  doc.roundedRect(x, y, w, HEAD_H, 2.5, 2.5, 'F');
  doc.setFillColor(30, 64, 175);
  doc.rect(x, y + HEAD_H - 2.5, w, 2.5, 'F');

  if (selloData) {
    try { doc.addImage(selloData, 'PNG', x + 1.5, y + 1.5, 6, 6); } catch { /* ignore */ }
  }
  const textX = x + (selloData ? 9 : 2.5);
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  const nombreInst = institucion.length > 28 ? `${institucion.slice(0, 26)}…` : institucion;
  doc.text(nombreInst.toUpperCase(), textX, y + 4);
  doc.setFontSize(5);
  doc.setFont('helvetica', 'normal');
  doc.text(`Año Escolar ${est.anio}`, textX, y + 7);

  // Foto centrada arriba
  const PW = 30, PH = 32;
  const PX = x + (w - PW) / 2;
  const PY = y + HEAD_H + 3;
  if (fotoData) {
    try {
      doc.setFillColor(241, 245, 249);
      doc.roundedRect(PX, PY, PW, PH, 2, 2, 'F');
      doc.addImage(fotoData, 'JPEG', PX, PY, PW, PH);
    } catch {
      drawAvatarIniciales(doc, est, PX, PY, PW, PH);
    }
  } else {
    drawAvatarIniciales(doc, est, PX, PY, PW, PH);
  }

  // Apellidos + Nombres
  let cy = PY + PH + 4;
  const TXT_X = x + 3;
  const TXT_W = w - 6;

  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  const ap = doc.splitTextToSize(est.estudianteApellidos.toUpperCase(), TXT_W);
  doc.text(ap.slice(0, 1), x + w / 2, cy, { align: 'center' });
  cy += 3.5;

  doc.setFontSize(7);
  doc.setFont('helvetica', 'normal');
  const nm = doc.splitTextToSize(est.estudianteNombres, TXT_W);
  doc.text(nm.slice(0, 1), x + w / 2, cy, { align: 'center' });
  cy += 3;

  // Línea separadora
  doc.setDrawColor(226, 232, 240);
  doc.setLineWidth(0.15);
  doc.line(TXT_X + 4, cy, TXT_X + TXT_W - 4, cy);
  cy += 2.5;

  // Datos compactos
  doc.setTextColor(100, 116, 139);
  doc.setFontSize(5);
  doc.text('DNI', TXT_X, cy);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6.5);
  doc.text(est.estudianteDni, TXT_X + 8, cy);
  cy += 3;

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(5);
  doc.text('GRADO', TXT_X, cy);
  doc.setTextColor(15, 23, 42);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(6);
  doc.text(`${est.gradoNombre} ${est.seccionNombre} · ${est.nivel}`, TXT_X + 8, cy);

  // QR centrado al pie
  const QR_SIZE = 18;
  const QR_X = x + (w - QR_SIZE) / 2;
  const QR_Y = y + h - QR_SIZE - 5;
  try { doc.addImage(qrData, 'PNG', QR_X, QR_Y, QR_SIZE, QR_SIZE); } catch { /* ignore */ }
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(4);
  doc.text('Escanear DNI', x + w / 2, y + h - 2, { align: 'center' });
}

// ── Función principal — fotochecks múltiples ──────────────────

export async function generarFotocheckPDF(
  estudiantes: FotocheckEstudiante[],
  nombreArchivo: string = 'fotochecks',
  orientacion: OrientacionFotocheck = 'horizontal',
): Promise<void> {
  if (estudiantes.length === 0) return;

  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const institucion = getConfig().institucionNombre || 'Institución Educativa';
  const selloData = catalogStorage.getSelloBase64();

  // Pre-cargar QRs y fotos en paralelo
  const datos = await Promise.all(estudiantes.map(async est => {
    const [qrData, fotoData] = await Promise.all([
      generarQR(est.estudianteDni),
      resolverFotoDataUrl(est.fotoPerfil, est.estudianteId),
    ]);
    return { est, qrData, fotoData };
  }));

  // Layout según orientación
  const isVertical = orientacion === 'vertical';
  const cardW    = isVertical ? V_CARD_W   : H_CARD_W;
  const cardH    = isVertical ? V_CARD_H   : H_CARD_H;
  const cols     = isVertical ? V_COLS     : H_COLS;
  const rows     = isVertical ? V_ROWS     : H_ROWS;
  const marginX  = isVertical ? V_MARGIN_X : H_MARGIN_X;
  const marginY  = isVertical ? V_MARGIN_Y : H_MARGIN_Y;
  const render   = isVertical ? renderFotocheckVertical : renderFotocheck;

  const porPagina = cols * rows;
  for (let i = 0; i < datos.length; i++) {
    if (i > 0 && i % porPagina === 0) doc.addPage();
    const local = i % porPagina;
    const fila  = Math.floor(local / cols);
    const col   = local % cols;
    const x = marginX + col  * (cardW + marginX);
    const y = marginY + fila * (cardH + marginY);
    render({
      doc,
      est:      datos[i].est,
      qrData:   datos[i].qrData,
      fotoData: datos[i].fotoData,
      x, y, w: cardW, h: cardH,
      institucion,
      selloData,
    });
  }

  doc.save(`${nombreArchivo}.pdf`);
}

// ── Versión individual — 1 fotocheck a tamaño real (PVC 86×54 mm) ─

/** Marcas de corte tenues fuera de las 4 esquinas de la tarjeta. */
function dibujarMarcasCorte(doc: jsPDF, x: number, y: number, w: number, h: number): void {
  const len = 4;       // longitud del trazo
  const gap = 2;       // separación de la tarjeta
  doc.setDrawColor(148, 163, 184);
  doc.setLineWidth(0.15);
  // esquina sup-izq
  doc.line(x - gap - len, y,           x - gap,        y);
  doc.line(x,             y - gap - len, x,            y - gap);
  // esquina sup-der
  doc.line(x + w + gap,   y,           x + w + gap + len, y);
  doc.line(x + w,         y - gap - len, x + w,           y - gap);
  // esquina inf-izq
  doc.line(x - gap - len, y + h,       x - gap,        y + h);
  doc.line(x,             y + h + gap, x,              y + h + gap + len);
  // esquina inf-der
  doc.line(x + w + gap,   y + h,       x + w + gap + len, y + h);
  doc.line(x + w,         y + h + gap, x + w,             y + h + gap + len);
}

export async function generarFotocheckIndividualPDF(
  est: FotocheckEstudiante,
  orientacion: OrientacionFotocheck = 'horizontal',
): Promise<void> {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const institucion = getConfig().institucionNombre || 'Institución Educativa';
  const selloData = catalogStorage.getSelloBase64();

  const [qrData, fotoData] = await Promise.all([
    generarQR(est.estudianteDni),
    resolverFotoDataUrl(est.fotoPerfil, est.estudianteId),
  ]);

  // Tarjeta a tamaño real PVC (86 × 54 horizontal · 54 × 86 vertical)
  const isVertical = orientacion === 'vertical';
  const W = isVertical ? V_CARD_W : H_CARD_W;
  const H = isVertical ? V_CARD_H : H_CARD_H;
  const x = (A4_W - W) / 2;
  const y = 50;   // centrada con espacio para título arriba e instrucciones abajo

  // Título
  doc.setTextColor(30, 64, 175);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.text('FOTOCHECK ESTUDIANTIL', A4_W / 2, 24, { align: 'center' });

  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(institucion, A4_W / 2, 30, { align: 'center' });

  doc.setFontSize(8);
  doc.text(
    `Tamaño real: ${W} × ${H} mm (PVC) · ${isVertical ? 'Vertical' : 'Horizontal'}`,
    A4_W / 2, 36,
    { align: 'center' },
  );

  // Marcas de corte y render a tamaño real
  dibujarMarcasCorte(doc, x, y, W, H);
  const render = isVertical ? renderFotocheckVertical : renderFotocheck;
  render({ doc, est, qrData, fotoData, x, y, w: W, h: H, institucion, selloData });

  // Pie con instrucciones
  doc.setTextColor(100, 116, 139);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  doc.text(
    'Recorta la tarjeta por el borde y plastifícala o insértala en un porta-credencial PVC.',
    A4_W / 2, y + H + 14,
    { align: 'center' },
  );
  doc.setFontSize(8);
  doc.text(
    'Escanea el código QR con la cámara del celular para registrar la asistencia diaria.',
    A4_W / 2, y + H + 20,
    { align: 'center' },
  );

  doc.save(`fotocheck-${est.estudianteDni}-${orientacion}.pdf`);
}

// Las versiones individuales ahora usan los mismos renders a tamaño real.
// (Los renderers "grandes" anteriores fueron eliminados por innecesarios.)
